import { z } from 'zod'
import {
  RegionSchema,
  DMSSchema,
  ConnectedDMSSchema,
  TokenSchema,
  UserSchema,
  OrganizationSchema,
  DealUsershipSchema,
  DMSDocumentSchema,
  DealSchemaForDMS,
  DealStatusSchema,
  ServiceSchema,
  CurrentUserSchema,
  ClosingBookSchema,
  NewClosingBookDataSchema,
  ClosingBookDocumentSetSchema,
  ClosingBookEventSchema,
  ClosingBookTemplateSchema,
  ClosingBookPreviewJobsSchema,
  RoleSchema,
  DealroomUserSchema,
  AgendaObjectCBSchema,
  AgendaObjectServerSchema,
  AgendaColumnSchema,
  ItemSchema,
  ActionItemSchema,
  ActionItemStatusSchema,
  CategorySchema,
  RedactedItemSchema,
  RedactedActionItemSchema,
  RedactedCategorySchema,
  UploadSchema,
  FileMatchSchema,
  FirmSchema,
  AgendaSchema,
  RegistrationSchema,
  DealInviteSchema,
  DealSchema,
  UploadRawUrlSchema,
  ClosingVersionSchema,
  SnapshotSchema,
  RedactedSnapshotSchema,
  MarkupSchema,
  BlacklineSchema,
  VersionSchema,
  CreateVersionPayloadSchema,
  VersionCellDataSchema,
  OrganizationRegistrationSchema,
  AccessCellDataSchema,
  AuditTokenSchema,
  AllTrashSchema,
  TrashedUploadSchema,
  TrashedVersionSchema,
  TrashedItemSchema,
  TrashedActionItemSchema,
  TrashedCategorySchema,
  TrashedMarkupSchema,
  TrashedFirmSchema,
  BulkUntrashResponseSchema,
  BulkUntrashPayloadSchema,
  VersionMinimalSchema,
  ResponsibleLawyerSchema,
  EmbeddedUserSchema,
  TitleCellDataSchema,
  ResponsibilityCellDataSchema,
  ActionItemCellDataSchema,
  SignatureCellDataSchema,
  ScheduleCellDataSchema,
  NoteCellDataSchema,
  TextCellDataSchema,
  NumberCellDataSchema,
  DateCellDataSchema,
  DealVariableSchema,
  ItemWithVariableSchema,
  ItemVariableSchema,
  AgendaNoteSchema,
  RenderedVersionSchema,
  VersionRenderedVariableSchema,
  VersionDetectedVariableSchema,
  ScheduleSchema,
  SignatureSchema,
  SignaturePageSchema,
  SignaturePageTemplateSchema,
  SignatureMatchSchema,
  DetectedSignatureSchema,
  AddSignaturesRequestSchema,
  AddSignaturesResponseSchema,
  UpdateSignatureCoordinatesSchema,
  EntitySchema,
  IndividualSchema,
  SigningPackageSchema,
  SigningPackageSetSchema,
  PerSignatorySendSchema,
  SigningPackageCompilationRequestSchema,
  SigningPositionSchema,
  BulkCreateIndividualItemSchema,
  BulkCreateIndividualsRequestSchema,
  BulkCreateIndividualsResponseSchema,
  BulkCreateEntityItemSchema,
  BulkCreateEntitiesRequestSchema,
  BulkCreateEntitiesResponseSchema,
  AgendaColumnEntrySchema,
  CustomEntryCellDataSchema,
  SelectOptionSchema,
  DealFieldSchema,
  TextConfigSchema,
  NumberConfigSchema,
  SingleSelectConfigSchema,
  CustomDealFieldValueSchema,
  CustomDealFieldSetSchema,
  ExportRequestSchema,
  SendDocumentsRequestSchema,
  SendDocumentsAttachmentSchema,
  AttachmentTypeSchema,
  AttachmentFormatSchema,
  ItemApprovalSchema,
  ApprovalGroupSchema,
  ApproverSchema,
  RedactedItemApprovalSchema,
  RedactedApproverSchema,
  BulkCreateItemApprovalsRequestSchema,
  ManageApproversRequestSchema,
  SendApprovalRequestsRequestSchema,
  EditApprovalMessageRequestSchema,
  RejectApproverRequestSchema,
  ApprovalsBatchPayloadSchema,
  DealCreateSchema,
  DealInviteDomainSchema,
  SaveAsTemplatePayloadSchema,
  DealTemplateSchema,
  BulkMarkFinalPayloadSchema,
  BulkCompilePageRangeSchema,
  BulkCompilePageRangeResponseSchema,
  BulkCompileItemSchema,
  BulkCompileItemResponseSchema,
  BulkCompileClosingVersionPayloadSchema,
  UpdateActionItemStatusesPayloadSchema,
  DeleteActionItemStatusesPayloadSchema,
  CreateNotePayloadSchema,
  CreateSnapshotPayloadSchema,
  CreateBlacklinePayloadSchema,
  UpdatePositionsPayloadSchema,
  RerankAgendaObjectsPayloadSchema,
  UpdateNumberingPayloadSchema,
  SetAgendaFirmsPayloadSchema,
  SetItemsCtypePayloadSchema,
  ContentTypesSchema,
  AgendaObjectConversionPayloadSchema,
  SerializedAgendaObjectSchema,
  BulkAddAgendaObjectsPayloadSchema,
  DeleteAgendaColumnsPayloadSchema,
  CreateAgendaColumnsPayloadSchema,
  ModifyAgendaColumnsPayloadSchema,
} from './schemas'
import { DealListItemWithUsership } from './api/deals'

export type InferApiResponse<AsyncFn> = AsyncFn extends (
  ...args: infer _
) => Promise<infer ResponseType>
  ? ResponseType
  : never

export enum UserState {
  ACTIVE = 'active',
  DEACTIVATED = 'deactivated',
  DELETED = 'deleted',
  INVITED = 'invited', // Only received from the user stats endpoint, these are email registrations, but are not users yet
}

export type Deal = z.infer<typeof DealSchema>
export type DealCreate = z.infer<typeof DealCreateSchema>
export type SaveAsTemplatePayload = z.infer<typeof SaveAsTemplatePayloadSchema>
export type DealTemplate = z.infer<typeof DealTemplateSchema>
export type DealType = z.infer<typeof DealSchemaForDMS>
export type DealStatus = z.infer<typeof DealStatusSchema>
export type Region = z.infer<typeof RegionSchema>
export type DMS = z.infer<typeof DMSSchema>
export type ConnectedDMS = z.infer<typeof ConnectedDMSSchema>
export type DMSDocument = z.infer<typeof DMSDocumentSchema>
export type Service = z.infer<typeof ServiceSchema>

export type AuthToken = z.infer<typeof TokenSchema>
export type AuthUserData = z.infer<typeof UserSchema>
export type AuthCurrentUser = z.infer<typeof CurrentUserSchema>
export type AuthOrganization = z.infer<typeof OrganizationSchema>
export type Registration = z.infer<typeof RegistrationSchema>
export type OrganizationRegistration = z.infer<typeof OrganizationRegistrationSchema>

export type DealUsership = z.infer<typeof DealUsershipSchema>
export type DealInvite = z.infer<typeof DealInviteSchema>
export type DealInviteDomain = z.infer<typeof DealInviteDomainSchema>
export type MergedDealProps = DealListItemWithUsership
export type DealroomUserData = z.infer<typeof DealroomUserSchema>
export type User = z.infer<typeof UserSchema>
export type EmbeddedUser = z.infer<typeof EmbeddedUserSchema>
export type ResponsibleLawyer = z.infer<typeof ResponsibleLawyerSchema>
export type Firm = z.infer<typeof FirmSchema>

export type FeatureFlagName =
  | 'maintenance_banner'
  | 'hide_maintenance_banner'
  | 'imanage10'
  | 'imanage_10_enabled'
  | 'beamer'
  | 'adobe_sign'
  | 'hide_docusign'
  | 'onespan'
  | 'bada_audit'
  | 'minimal_versions'
  | 'llm_detection_button'
  | 'llm_agenda_importer'
  | 'llm_agenda_import_deal_specific'
  | 'hide_feature_opt_in_banner'
  | 'llm_opt_out'
  | 'llm_agenda_import_opt_out'
  | 'react_dealroom_overview'
  | 'react_ai_importer'
  | 'react_agenda_importer'
  | 'dealroom_increase_custom_columns'
  | 'react_approvals_wizard'
  | 'react_parties_wizard'
  | 'system_deal_fields'
  | 'dark_mode'
  | 'disable_support_sending'
  | 'disable_support_agenda_imports'
  | 'maintenance_banner_au' // Evergreen
  | 'maintenance_banner_eu' // Evergreen
  | 'maintenance_banner_na' // Evergreen
  | 'invite_allowlist' // Evergreen
  | 'mark_executed'
  | 'additional_signing_officers'
  | 'improved_non_ai_flow'
  | 'signing_evidence'
  | 'service_account'
  | 'signatory_metadata'
  | 'multi_dms_connections'

export type ClosingBook = z.infer<typeof ClosingBookSchema>
export type NewClosingBookData = z.infer<typeof NewClosingBookDataSchema>
export type ClosingBookDocumentSet = z.infer<typeof ClosingBookDocumentSetSchema>
export type ClosingBookEvent = z.infer<typeof ClosingBookEventSchema>
export type ClosingBookTemplate = z.infer<typeof ClosingBookTemplateSchema>
export type ClosingBookPreviewJobs = z.infer<typeof ClosingBookPreviewJobsSchema>

export type Role = z.infer<typeof RoleSchema>

export type Upload = z.infer<typeof UploadSchema>
export type UploadRawUrl = z.infer<typeof UploadRawUrlSchema>
export type FileMatch = z.infer<typeof FileMatchSchema>

export type AgendaObject = z.infer<typeof AgendaObjectCBSchema>
export type AgendaObjectServer = z.infer<typeof AgendaObjectServerSchema>
export type AgendaColumn = z.infer<typeof AgendaColumnSchema>
export type AgendaColumnEntry = z.infer<typeof AgendaColumnEntrySchema>
export type AgendaType = z.infer<typeof AgendaSchema>
export type Item = z.infer<typeof ItemSchema>
export type ActionItem = z.infer<typeof ActionItemSchema>
export type ActionItemStatus = z.infer<typeof ActionItemStatusSchema>
export type Category = z.infer<typeof CategorySchema>
export type RedactedItem = z.infer<typeof RedactedItemSchema>
export type RedactedActionItem = z.infer<typeof RedactedActionItemSchema>
export type RedactedCategory = z.infer<typeof RedactedCategorySchema>
export type ClosingVersion = z.infer<typeof ClosingVersionSchema>
export type Snapshot = z.infer<typeof SnapshotSchema>
export type RedactedSnapshot = z.infer<typeof RedactedSnapshotSchema>
export type Markup = z.infer<typeof MarkupSchema>
export type Blackline = z.infer<typeof BlacklineSchema>
export type ExportRequest = z.infer<typeof ExportRequestSchema>
export type SendDocumentsRequest = z.infer<typeof SendDocumentsRequestSchema>
export type SendDocumentsAttachment = z.infer<typeof SendDocumentsAttachmentSchema>
export type AttachmentType = z.infer<typeof AttachmentTypeSchema>
export type AttachmentFormat = z.infer<typeof AttachmentFormatSchema>
export type Version = z.infer<typeof VersionSchema>
export type VersionMinimal = z.infer<typeof VersionMinimalSchema>
export type VersionCellData = z.infer<typeof VersionCellDataSchema>
export type AccessCellData = z.infer<typeof AccessCellDataSchema>
export type TitleCellData = z.infer<typeof TitleCellDataSchema>
export type ActionItemCellData = z.infer<typeof ActionItemCellDataSchema>
export type NoteCellData = z.infer<typeof NoteCellDataSchema>
export type SignatureCellData = z.infer<typeof SignatureCellDataSchema>
export type ScheduleCellData = z.infer<typeof ScheduleCellDataSchema>
export type ResponsibilityCellData = z.infer<typeof ResponsibilityCellDataSchema>
export type DateCellData = z.infer<typeof DateCellDataSchema>
export type CustomEntryCellData = z.infer<typeof CustomEntryCellDataSchema>
export type TextCellData = z.infer<typeof TextCellDataSchema>
export type NumberCellData = z.infer<typeof NumberCellDataSchema>
export type DealVariableType = z.infer<typeof DealVariableSchema>
export type ItemVariable = z.infer<typeof ItemVariableSchema>
export type ItemWithVariable = z.infer<typeof ItemWithVariableSchema>
export type AgendaNote = z.infer<typeof AgendaNoteSchema>
export type Schedule = z.infer<typeof ScheduleSchema>
export type Signature = z.infer<typeof SignatureSchema>
export type SignaturePage = z.infer<typeof SignaturePageSchema>
export type SignaturePageTemplate = z.infer<typeof SignaturePageTemplateSchema>
export type SignatureMatch = z.infer<typeof SignatureMatchSchema>
export type DetectedSignature = z.infer<typeof DetectedSignatureSchema>
export type AddSignaturesRequest = z.infer<typeof AddSignaturesRequestSchema>
export type AddSignaturesResponse = z.infer<typeof AddSignaturesResponseSchema>
export type UpdateSignatureCoordinates = z.infer<typeof UpdateSignatureCoordinatesSchema>
export type Entity = z.infer<typeof EntitySchema>
export type Individual = z.infer<typeof IndividualSchema>
export type SigningPackage = z.infer<typeof SigningPackageSchema>
export type SigningPackageSet = z.infer<typeof SigningPackageSetSchema>
export type PerSignatorySend = z.infer<typeof PerSignatorySendSchema>
export type SigningPackageCompilationRequest = z.infer<
  typeof SigningPackageCompilationRequestSchema
>
export type SigningPackageCompilationService = 'pdf' | 'docusign' | 'onespan' | 'adobe'
export type SigningPosition = z.infer<typeof SigningPositionSchema>
export type RenderedVersion = z.infer<typeof RenderedVersionSchema>
export type VersionRenderedVariable = z.infer<typeof VersionRenderedVariableSchema>
export type VersionDetectedVariable = z.infer<typeof VersionDetectedVariableSchema>

export type AuditToken = z.infer<typeof AuditTokenSchema>

export type AllTrash = z.infer<typeof AllTrashSchema>
export type TrashedUpload = z.infer<typeof TrashedUploadSchema>
export type TrashedVersion = z.infer<typeof TrashedVersionSchema>
export type TrashedItem = z.infer<typeof TrashedItemSchema>
export type TrashedActionItem = z.infer<typeof TrashedActionItemSchema>
export type TrashedCategory = z.infer<typeof TrashedCategorySchema>
export type TrashedMarkup = z.infer<typeof TrashedMarkupSchema>
export type TrashedFirm = z.infer<typeof TrashedFirmSchema>
export type BulkUntrashPayload = z.infer<typeof BulkUntrashPayloadSchema>
export type BulkUntrashResponse = z.infer<typeof BulkUntrashResponseSchema>
export type BulkEmptyTrashPayload = BulkUntrashPayload
export type BulkTrashPayload = BulkUntrashPayload

export type BulkMarkFinalPayload = z.infer<typeof BulkMarkFinalPayloadSchema>
export type BulkCompilePageRange = z.infer<typeof BulkCompilePageRangeSchema>
export type BulkCompilePageRangeResponse = z.infer<typeof BulkCompilePageRangeResponseSchema>
export type BulkCompileItem = z.infer<typeof BulkCompileItemSchema>
export type BulkCompileItemResponse = z.infer<typeof BulkCompileItemResponseSchema>
export type BulkCompileClosingVersionPayload = z.infer<
  typeof BulkCompileClosingVersionPayloadSchema
>

export type ItemPayload = Item & { responsible_firm_ids?: number[] }
export type ActionItemPayload = ActionItem & { responsible_firm_ids?: number[] }
export type UpdateActionItemStatusesPayload = z.infer<typeof UpdateActionItemStatusesPayloadSchema>
export type DeleteActionItemStatusesPayload = z.infer<typeof DeleteActionItemStatusesPayloadSchema>
export type CreateVersionPayload = z.infer<typeof CreateVersionPayloadSchema>
export type CreateNotePayload = z.infer<typeof CreateNotePayloadSchema>
export type CreateSnapshotPayload = z.infer<typeof CreateSnapshotPayloadSchema>
export type CreateBlacklinePayload = z.infer<typeof CreateBlacklinePayloadSchema>
export type UpdatePositionsPayload = z.infer<typeof UpdatePositionsPayloadSchema>
export type CopyAgendaObjectsPayload = UpdatePositionsPayload
export type RerankAgendaObjectsPayload = z.infer<typeof RerankAgendaObjectsPayloadSchema>
export type UpdateNumberingPayload = z.infer<typeof UpdateNumberingPayloadSchema>
export type SetAgendaFirmsPayload = z.infer<typeof SetAgendaFirmsPayloadSchema>
export type SetItemsCtypePayload = z.infer<typeof SetItemsCtypePayloadSchema>
export type ContentTypes = z.infer<typeof ContentTypesSchema>
export type AgendaObjectConversionPayload = z.infer<typeof AgendaObjectConversionPayloadSchema>

// Bulk Add Payloads
export type SerializedAgendaObject = z.infer<typeof SerializedAgendaObjectSchema>
export type BulkAddAgendaObjectsPayload = z.infer<typeof BulkAddAgendaObjectsPayloadSchema>
export type DeleteAgendaColumnsPayload = z.infer<typeof DeleteAgendaColumnsPayloadSchema>
export type CreateAgendaColumnsPayload = z.infer<typeof CreateAgendaColumnsPayloadSchema>

export type CompleteAgendaImportPayload = {
  headers: {
    id: string
    label: string
    type: string
  }[]
}

export type ModifyAgendaColumnsPayload = z.infer<typeof ModifyAgendaColumnsPayloadSchema>

export type SelectOption = z.infer<typeof SelectOptionSchema>
export type DealField = z.infer<typeof DealFieldSchema>
export type DealFieldType = DealField['deal_field_type']
export type DealFieldState = DealField['state']
export type TextConfig = z.infer<typeof TextConfigSchema>
export type NumberConfig = z.infer<typeof NumberConfigSchema>
export type SingleSelectConfig = z.infer<typeof SingleSelectConfigSchema>
export type CustomDealFieldValue = z.infer<typeof CustomDealFieldValueSchema>
export type CustomDealFieldSet = z.infer<typeof CustomDealFieldSetSchema>

export type ItemApproval = z.infer<typeof ItemApprovalSchema>
export type ApprovalGroup = z.infer<typeof ApprovalGroupSchema>
export type Approver = z.infer<typeof ApproverSchema>
export type RedactedItemApproval = z.infer<typeof RedactedItemApprovalSchema>
export type RedactedApprover = z.infer<typeof RedactedApproverSchema>
export type BulkCreateItemApprovalsRequest = z.infer<typeof BulkCreateItemApprovalsRequestSchema>
export type ManageApproversRequest = z.infer<typeof ManageApproversRequestSchema>
export type SendApprovalRequestsRequest = z.infer<typeof SendApprovalRequestsRequestSchema>
export type EditApprovalMessageRequest = z.infer<typeof EditApprovalMessageRequestSchema>
export type RejectApproverRequest = z.infer<typeof RejectApproverRequestSchema>
export type ApprovalsBatchPayload = z.infer<typeof ApprovalsBatchPayloadSchema>

export type BulkCreateIndividualItem = z.infer<typeof BulkCreateIndividualItemSchema>
export type BulkCreateIndividualsRequest = z.infer<typeof BulkCreateIndividualsRequestSchema>
export type BulkCreateIndividualsResponse = z.infer<typeof BulkCreateIndividualsResponseSchema>
export type BulkCreateEntityItem = z.infer<typeof BulkCreateEntityItemSchema>
export type BulkCreateEntitiesRequest = z.infer<typeof BulkCreateEntitiesRequestSchema>
export type BulkCreateEntitiesResponse = z.infer<typeof BulkCreateEntitiesResponseSchema>
